import { useState } from "react";
import { GroupedInvestmentCategory } from "../../types";
import { SVG_ENUM } from "enums";
import SVG from "shared/SVG";
import ContextMenu from "shared/ContextMenu";
import InvestmentPhotoLightbox from "../InvestmentPhotoLightbox/InvestmentPhotoLightbox";
import "./AssetTile.scss";

interface AssetTileProps {
  category: GroupedInvestmentCategory;
  index: number;
  onToggleExpand: (slug: string, isExpanded: boolean) => void;
  onDragStart: (index: number) => void;
  onDragEnter: (index: number) => void;
  onDragEnd: () => void;
  isDragging: boolean;
  isDragOver: boolean;
}

const AssetTile = ({
  category,
  index,
  onToggleExpand,
  onDragStart,
  onDragEnter,
  onDragEnd,
  isDragging,
  isDragOver,
}: AssetTileProps) => {
  const { investmentType, isExpanded, items } = category;
  const [lightboxItem, setLightboxItem] = useState<any>(null);

  const handleToggle = () => {
    onToggleExpand(investmentType.slug, !isExpanded);
  };

  type ColumnDef = {
    key: string;
    width?: number;
    suffix?: string;
    label?: string;
  };

  const getColumns = (): ColumnDef[] => {
    switch (investmentType.slug) {
      case "stocks":
      case "shares":
        return [
          { key: "quantity", width: 150 },
          { key: "name", width: 250 },
          { key: "purchaseRate", width: 200 },
        ];
      case "crypto":
        return [
          { key: "quantity", width: 130 },
          { key: "name", width: 110 },
          {
            key: "primaryValue",
            width: 200,
            suffix: "%",
            label: "Kurs zakupu",
          },
        ];
      case "currencies":
        return [
          { key: "name", width: 200 },
          { key: "primaryValue", width: 200 },
          { key: "purchaseRate", width: 200, suffix: "%" },
        ];
      case "treasury_bonds":
        return [
          { key: "quantity", width: 120 },
          { key: "primaryValue", width: 150 },
          { key: "interestRate", width: 220 },
          { key: "interestType", width: 220 },
          { key: "currencyUuid", width: 100 },
        ];
      case "precious_stones":
        return [
          { key: "name", width: 150 },
          { key: "primaryValue", width: 150 },
          { key: "currencyUuid", width: 100 },
          { key: "weight", width: 100, suffix: "g" },
          { key: "cut", width: 100 },
          { key: "color", width: 100 },
          { key: "clarity", width: 100 },
        ];
      case "precious_metals":
        return [
          { key: "name", width: 200 },
          { key: "primaryValue", width: 200 },
          { key: "currencyUuid", width: 100 },
          { key: "weight", width: 120, suffix: "g" },
          { key: "finess", width: 100 },
        ];
      case "artwork":
        return [
          { key: "name", width: 200 },
          { key: "primaryValue", width: 200 },
          { key: "currencyUuid", width: 100 },
          { key: "author", width: 200 },
          { key: "eraAge", width: 150 },
        ];
      case "real_estate":
        return [
          { key: "name", width: 200 },
          { key: "primaryValue", width: 180, label: "Cena zakupu" },
          { key: "currencyUuid", width: 150 },
          { key: "area", width: 200 },
          { key: "pricePerSqm", width: 180, label: "Cena za m2:" },
        ];
      case "loans":
        return [
          { key: "primaryValue", width: 150 },
          { key: "currencyUuid", width: 100 },
          { key: "interestRate", width: 120 },
          { key: "interestType", width: 110 },
          { key: "interestPayment", width: 160 },
          { key: "contractDate", width: 150 },
          { key: "durationDate", width: 150 },
          { key: "autoRenewal", width: 80 },
        ];
      case "investment_policy":
        return [
          { key: "name", width: 200 },
          { key: "currencyUuid", width: 100 },
          { key: "contractDate", width: 150 },
          { key: "durationDate", width: 150 },
          { key: "premiumPayment", width: 150 },
        ];
      default:
        return [
          { key: "name" },
          { key: "primaryValue" },
          { key: "currencyUuid" },
        ];
    }
  };

  const columnLabels: Record<string, string> = {
    quantity: "Ilość",
    name: "Nazwa",
    purchaseRate: "Kurs zakupu",
    primaryValue: "Kwota",
    currencyUuid: "Waluta",
    area: "Wielkość [w m2]",
    interestRate: "Wysokość oprocentowania",
    interestType: "Rodzaj oprocentowania",
    interestPayment: "Odsetki",
    weight: "Masa",
    cut: "Szlif",
    color: "Barwa",
    clarity: "Czystość",
    finess: "Próba",
    author: "Autor",
    eraAge: "Epoka/wiek",
    contractDate: "Data zawarcia",
    durationDate: "Czas trwania",
    premiumPayment: "Składki",
    autoRenewal: "Automatyczne przedłużenie",
  };

  const columns = getColumns();
  const hasFixedWidths = columns.some((col) => col.width !== undefined);

  const INTEREST_TYPE_LABELS: Record<string, string> = {
    fixed: "Stałe",
    variable: "Zmienne",
    periodically_fixed: "Okresowo-stałe",
  };

  const PAYMENT_LABELS: Record<string, string> = {
    monthly: "Miesięczne",
    quarterly: "Kwartalne",
    yearly: "Roczne",
    at_end_of_year: "Na koniec roku",
  };

  const detailsFields = [
    "area",
    "weight",
    "cut",
    "color",
    "clarity",
    "finess",
    "author",
    "eraAge",
    "interestType",
    "interestPayment",
    "contractDate",
    "durationDate",
    "premiumPayment",
    "autoRenewal",
  ];

  const PROPERTY_TYPE_LABELS: Record<string, string> = {
    apartment: "Mieszkanie",
    house: "Dom",
    land: "Działka",
  };

  const METAL_TYPE_LABELS: Record<string, string> = {
    gold: "Złoto",
    silver: "Srebro",
  };

  const formatValue = (item: any, column: string) => {
    if (column === "currencyUuid") {
      const currency = item.currency?.currency;
      return currency ?? "—";
    }

    if (column === "name" && investmentType.slug === "currencies") {
      return item.currency?.currency ?? "—";
    }

    if (
      column === "interestRate" &&
      (investmentType.slug === "treasury_bonds" ||
        investmentType.slug === "loans")
    ) {
      const value = item.details?.interestRate;
      if (value === null || value === undefined || value === "") return "—";
      return `${value}%`;
    }

    if (column === "interestType") {
      const value =
        item.details?.interestRateType ?? item.details?.interestType;
      if (value === null || value === undefined || value === "") return "—";
      return INTEREST_TYPE_LABELS[value] ?? value;
    }

    if (column === "name" && investmentType.slug === "real_estate") {
      const propertyType = item.details?.propertyType;
      if (!propertyType) return item.name ?? "—";
      if (propertyType === "other") {
        return item.details?.propertyTypeOther ?? "—";
      }
      return PROPERTY_TYPE_LABELS[propertyType] ?? propertyType;
    }

    if (column === "name" && investmentType.slug === "precious_metals") {
      const metalType = item.details?.metalType;
      if (!metalType) return item.name ?? "—";
      if (metalType === "other") {
        return item.details?.metalTypeOther ?? "—";
      }
      return METAL_TYPE_LABELS[metalType] ?? metalType;
    }

    if (column === "pricePerSqm") {
      const sqmValue = item.details?.pricePerSqm;
      if (sqmValue === null || sqmValue === undefined) return "—";
      return Number(sqmValue).toLocaleString("pl-PL", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    if (investmentType.slug === "investment_policy") {
      if (column === "contractDate") {
        const dateVal = item.details?.contractDate;
        if (!dateVal) return "—";
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? dateVal : d.toLocaleDateString("pl-PL");
      }

      if (column === "durationDate") {
        const dateVal = item.details?.durationDate;
        if (dateVal === null || dateVal === undefined) return "Bezterminowa";
        if (typeof dateVal === "string" && dateVal !== "") {
          const d = new Date(dateVal);
          if (isNaN(d.getTime())) return "—";
          if (
            d.getFullYear() === 2000 &&
            d.getMonth() === 0 &&
            d.getDate() === 1
          )
            return "—";
          return d.toLocaleDateString("pl-PL");
        }
        return "—";
      }
    }

    if (investmentType.slug === "loans") {
      if (column === "contractDate") {
        const dateVal = item.details?.contractDate;
        if (!dateVal) return "—";
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? dateVal : d.toLocaleDateString("pl-PL");
      }

      if (column === "durationDate") {
        const dateVal = item.details?.durationDate;
        if (dateVal === null || dateVal === undefined) return "Bezterminowa";
        if (typeof dateVal === "string" && dateVal !== "") {
          const d = new Date(dateVal);
          if (isNaN(d.getTime())) return "—";
          if (
            d.getFullYear() === 2000 &&
            d.getMonth() === 0 &&
            d.getDate() === 1
          )
            return "—";
          return d.toLocaleDateString("pl-PL");
        }
        return "—";
      }
    }

    const value = detailsFields.includes(column)
      ? item.details?.[column]
      : item[column];

    if (value === null || value === undefined || value === "") return "—";

    if (column === "interestRate") return `${value}%`;
    if (column === "interestPayment" || column === "premiumPayment")
      return PAYMENT_LABELS[value] ?? value;
    if (column === "autoRenewal")
      return value === true || value === 1 ? "Tak" : "Nie";
    if (column === "primaryValue") {
      return typeof value === "number"
        ? value.toLocaleString("pl-PL", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })
        : value;
    }
    if (column === "mass") return `${value}g`;

    return value;
  };

  return (
    <div
      className={`asset-tile ${isDragging ? "asset-tile--dragging" : ""} ${
        isDragOver ? "asset-tile--drag-over" : ""
      } ${isExpanded ? "asset-tile--expanded" : ""}`}
      draggable
      onDragStart={() => onDragStart(index)}
      onDragEnter={() => onDragEnter(index)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
    >
      <div className="asset-tile__header" onClick={handleToggle}>
        <div
          className="asset-tile__drag-handle"
          onClick={(e) => e.stopPropagation()}
        >
          <SVG type={SVG_ENUM.DRAG_HANDLE} />
        </div>
        <span className="asset-tile__title">{investmentType.name}</span>
        <div
          className={`asset-tile__chevron ${
            isExpanded ? "asset-tile__chevron--expanded" : ""
          }`}
        >
          <SVG type={SVG_ENUM.CHEVRON_DOWN} />
        </div>
      </div>

      <div
        className={`asset-tile__content-wrapper ${
          isExpanded ? "asset-tile__content-wrapper--expanded" : ""
        }`}
      >
        <div className="asset-tile__content-inner">
          {items.length > 0 && (
            <div className="asset-tile__content">
              <div className="asset-tile__table">
                <div className="asset-tile__table-header">
                  {columns.map((col) => (
                    <div
                      key={col.key}
                      className="asset-tile__table-cell"
                      style={
                        col.width
                          ? { flex: `0 0 ${col.width}px`, minWidth: col.width }
                          : undefined
                      }
                    >
                      {col.label ?? columnLabels[col.key] ?? col.key}
                    </div>
                  ))}
                  {hasFixedWidths && <div style={{ flex: 1 }} />}
                  <div className="asset-tile__table-cell asset-tile__table-cell--actions" />
                </div>
                {items.map((item) => {
                  const photoFiles =
                    investmentType.slug === "artwork" ||
                    investmentType.slug === "real_estate"
                      ? (item.files ?? []).filter(
                          (f: any) => f.role === "photo",
                        )
                      : [];

                  return (
                    <div key={item.uuid} className="asset-tile__table-row">
                      {columns.map((col) => (
                        <div
                          key={col.key}
                          className="asset-tile__table-cell"
                          style={
                            col.width
                              ? {
                                  flex: `0 0 ${col.width}px`,
                                  minWidth: col.width,
                                }
                              : undefined
                          }
                        >
                          {col.key === columns[0].key && (
                            <span className="asset-tile__info-icon">
                              <SVG type={SVG_ENUM.INFO} />
                            </span>
                          )}
                          {col.key === "name" &&
                          (investmentType.slug === "artwork" ||
                            investmentType.slug === "real_estate") ? (
                            <span className="asset-tile__name-with-preview">
                              <span>{formatValue(item, col.key)}</span>
                              {photoFiles.length > 0 && (
                                <button
                                  className="asset-tile__preview-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLightboxItem(item);
                                  }}
                                >
                                  podgląd
                                </button>
                              )}
                            </span>
                          ) : (
                            <>
                              {formatValue(item, col.key)}
                              {col.suffix && formatValue(item, col.key) !== "—"
                                ? col.suffix
                                : ""}
                            </>
                          )}
                        </div>
                      ))}
                      {hasFixedWidths && <div style={{ flex: 1 }} />}
                      <div className="asset-tile__table-cell asset-tile__table-cell--actions">
                        <button className="asset-tile__action-btn">
                          <SVG type={SVG_ENUM.ANNOUCEMENT} />
                          <span className="asset-tile__action-badge">0</span>
                        </button>
                        <ContextMenu data={item} position="left" items={[]}>
                          <button className="asset-tile__action-btn">
                            <SVG type={SVG_ENUM.DOTS} />
                          </button>
                        </ContextMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <InvestmentPhotoLightbox
        isOpen={!!lightboxItem}
        onClose={() => setLightboxItem(null)}
        files={(lightboxItem?.files ?? []).filter(
          (f: any) => f.role === "photo",
        )}
        title={lightboxItem?.name}
      />
    </div>
  );
};

export default AssetTile;
